import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild} from '@angular/core';
import {
  isShellContainerBreadcrumb,
  type ShellBreadcrumb,
  type ShellBreadcrumbTrailingActionTemplate,
  type ShellContainerBreadcrumb,
} from './shell-breadcrumbs.service';

const SIBLING_SWITCHER_LONG_PRESS_MS = 500;
const SIBLING_SWITCHER_MOVE_TOLERANCE_PX = 8;

export type ShellBreadcrumbLongPressEvent = {
  breadcrumb: ShellContainerBreadcrumb;
  event: PointerEvent;
};

type ShellBreadcrumbShapeVariant = 'single' | 'compact' | 'short' | 'medium' | 'long';

@Component({
  selector: 'app-shell-breadcrumbs',
  template: `
    @if (breadcrumbs.length) {
      <nav
        class="app-shell-breadcrumbs"
        [class.app-shell-breadcrumbs--collapsed]="isCollapsed"
        [class.app-shell-breadcrumbs--stable]="compactMode"
        [attr.aria-label]="ariaLabel">
        <div
          #trail
          class="app-shell-breadcrumbs__trail"
          [class.app-shell-breadcrumbs__trail--collapsed]="isCollapsed">
          @if (hasCollapsedAncestors) {
            <button #collapsedTrigger type="button" class="app-shell-breadcrumbs__collapsed-prefix app-shell-breadcrumbs__collapsed-trigger"
              [attr.aria-label]="expandLabel" [attr.aria-expanded]="fullPathOpen || relocationFullPathOpen" [attr.aria-controls]="'item-full-breadcrumb-path'"
              data-relocation-full-path-trigger=""
              (click)="openFullPath($event)">…</button>
          }

          @for (breadcrumb of visibleBreadcrumbs; track breadcrumb.id) {
            @let first = isFirstBreadcrumb(breadcrumb);
            @let last = isCurrentBreadcrumb(breadcrumb);
            @let shapeVariant = getBreadcrumbShapeVariant(breadcrumb.label, first, last);
            <span class="app-shell-breadcrumbs__item">
              <a
                class="app-shell-breadcrumbs__link"
                [class.app-shell-breadcrumbs__link--first]="first"
                [class.app-shell-breadcrumbs__link--current]="last"
                [class.app-shell-breadcrumbs__link--single]="first && last"
                [class.app-shell-breadcrumbs__link--container]="isContainerBreadcrumb(breadcrumb)"
                [attr.data-sibling-switcher-breadcrumb-id]="isContainerBreadcrumb(breadcrumb) ? breadcrumb.id : null"
                [attr.data-relocation-drop-target]="getRelocationDropTargetId(breadcrumb)"
                [attr.data-relocation-drop-accepts]="breadcrumb.relocationDropTarget?.accepts || null"
                [attr.aria-label]="first ? breadcrumb.label : null"
                [attr.aria-current]="last ? 'page' : null"
                [attr.draggable]="isContainerBreadcrumb(breadcrumb) ? 'false' : null"
                [attr.href]="getBreadcrumbHref(breadcrumb.route)"
                (click)="handleBreadcrumbClick($event, breadcrumb)"
                (contextmenu)="handleContextMenu($event, breadcrumb)"
                (pointerdown)="handlePointerDown($event, breadcrumb)"
                (pointermove)="handlePointerMove($event)"
                (pointerup)="handlePointerEnd($event)"
                (pointercancel)="handlePointerEnd($event)"
                (pointerleave)="handlePointerEnd($event)">
                <svg
                  class="app-shell-breadcrumbs__shape"
                  viewBox="0 0 200 40"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                  focusable="false">
                  <path
                    class="app-shell-breadcrumbs__shape-path"
                    [attr.d]="getBreadcrumbPath(shapeVariant)"
                  />
                </svg>

                <span class="app-shell-breadcrumbs__content">
                  @if (showBreadcrumbIcon(breadcrumb, first)) {
                    <span class="app-shell-breadcrumbs__icon material-symbols-outlined" aria-hidden="true">
                      home
                    </span>
                  }

                  @if (!first) {
                    <span class="app-shell-breadcrumbs__label">{{ breadcrumb.label }}</span>
                  }
                </span>
              </a>
            </span>
          }

          @if (compactMode && trailingActionTemplate) {
            <div #inlineActions class="app-shell-breadcrumbs__actions app-shell-breadcrumbs__actions--inline">
              <ng-container [ngTemplateOutlet]="trailingActionTemplate"></ng-container>
            </div>
          }
        </div>

        @if (compactMode) {
          <div #fullTrailMeasure class="app-shell-breadcrumbs__measure" aria-hidden="true" inert>
            @for (breadcrumb of breadcrumbs; track breadcrumb.id) {
              <span class="app-shell-breadcrumbs__item"><span class="app-shell-breadcrumbs__link">
                <span class="app-shell-breadcrumbs__content">
                  @if (isFirstBreadcrumb(breadcrumb)) {
                    <span class="app-shell-breadcrumbs__icon material-symbols-outlined">home</span>
                  } @else {
                    <span class="app-shell-breadcrumbs__label">{{ breadcrumb.label }}</span>
                  }
                </span>
              </span></span>
            }
          </div>
        }

        @if (!compactMode && trailingActionTemplate) {
          <div class="app-shell-breadcrumbs__actions">
            <ng-container [ngTemplateOutlet]="trailingActionTemplate"></ng-container>
          </div>
        }
      </nav>
        @if (compactMode) {
        <ion-popover #fullPathPopover class="app-shell-breadcrumbs__popover" [isOpen]="fullPathOpen" [event]="fullPathEvent"
          [style.--item-popover-anchor-bottom]="fullPathAnchorBottom + 'px'"
          side="bottom" alignment="start" (didPresent)="handleFullPathPresented()" (didDismiss)="handleFullPathDismissed()">
          <ng-template>
            <nav id="item-full-breadcrumb-path" #navigationFullPath class="app-shell-breadcrumbs__full-path" [attr.aria-label]="fullPathLabel">
              <ng-container [ngTemplateOutlet]="fullPathLinks"></ng-container>
            </nav>
          </ng-template>
        </ion-popover>
        @if (relocationFullPathOpen) {
          <nav #relocationFullPath class="app-shell-breadcrumbs__full-path app-shell-breadcrumbs__full-path--relocation"
            [style.left.px]="relocationFullPathLeft" [style.top.px]="relocationFullPathTop"
            [style.--relocation-full-path-top.px]="relocationFullPathTop"
            [attr.aria-label]="fullPathLabel" data-relocation-full-path-scroll="">
            <ng-container [ngTemplateOutlet]="fullPathLinks"></ng-container>
          </nav>
        }
        <ng-template #fullPathLinks>
          @for (breadcrumb of breadcrumbs; track breadcrumb.id) {
            <a class="app-shell-breadcrumbs__full-path-link"
              [class.app-shell-breadcrumbs__full-path-link--container]="isContainerBreadcrumb(breadcrumb)"
              [class.is-relocation-drop-target]="canAcceptRelocation(breadcrumb)"
              [attr.href]="getBreadcrumbHref(breadcrumb.route)"
              [attr.data-sibling-switcher-breadcrumb-id]="isContainerBreadcrumb(breadcrumb) ? breadcrumb.id : null"
              [attr.data-relocation-drop-target]="getRelocationDropTargetId(breadcrumb)"
              [attr.data-relocation-drop-accepts]="breadcrumb.relocationDropTarget?.accepts || null"
              [attr.aria-current]="isCurrentBreadcrumb(breadcrumb) ? 'page' : null"
              [attr.draggable]="isContainerBreadcrumb(breadcrumb) ? 'false' : null"
              (click)="handleFullPathClick($event, breadcrumb)"
              (contextmenu)="handleContextMenu($event, breadcrumb)"
              (pointerdown)="handlePointerDown($event, breadcrumb)"
              (pointermove)="handlePointerMove($event)"
              (pointerup)="handlePointerEnd($event)"
              (pointercancel)="handlePointerEnd($event)"
              (pointerleave)="handlePointerEnd($event)">
              @if (getFullPathIconName(breadcrumb); as iconName) {
                <ion-icon class="app-shell-breadcrumbs__full-path-icon" [name]="iconName" aria-hidden="true"></ion-icon>
              }
              <span>{{ breadcrumb.label }}</span>
            </a>
          }
        </ng-template>
      }
    }
  `,
  styleUrls: ['./shell-breadcrumbs.component.scss'],
  standalone: false,
})
export class ShellBreadcrumbsComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly compactBodyEndX = 150;
  private readonly shortBodyEndX = 170;
  private readonly mediumBodyEndX = 184;
  private readonly longBodyEndX = 190;
  private readonly edgeInsetX = 8;
  private readonly edgeTipX = 200;

  @Input() breadcrumbs: ShellBreadcrumb[] = [];
  @Input() ariaLabel = '';
  @Input() compactMode = false;
  @Input() collapsed = false;
  @Input() relocationMode = false;
  @Input() relocationSourceKind: 'container' | 'item' | null = null;
  @Input() relocationSourceId = '';
  @Input() iconResolver: (breadcrumb: ShellBreadcrumb) => string | null = () => null;
  @Input() expandLabel = '';
  @Input() fullPathLabel = '';
  @Input() routeHrefBuilder: (route: string) => string = (route) => route;
  @Input() trailingActionTemplate: ShellBreadcrumbTrailingActionTemplate = null;

  @Output() breadcrumbSelected = new EventEmitter<string>();
  @Output() breadcrumbLongPressed = new EventEmitter<ShellBreadcrumbLongPressEvent>();
  @Output() fullPathOpened = new EventEmitter<void>();
  @Output() fullPathClosed = new EventEmitter<void>();

  @ViewChild('trail') set trailElement(value: ElementRef<HTMLElement> | undefined) {
    if (this.trail?.nativeElement === value?.nativeElement) return;
    this.trailResizeObserver?.disconnect();
    this.trail = value;
    if (value && typeof ResizeObserver !== 'undefined') {
      this.trailResizeObserver = new ResizeObserver(() => this.measureFullTrail());
      this.trailResizeObserver.observe(value.nativeElement);
    }
    this.scheduleFullTrailMeasurement();
  }
  private trail?: ElementRef<HTMLElement>;
  @ViewChild('inlineActions') set inlineActionsElement(value: ElementRef<HTMLElement> | undefined) {
    if (this.inlineActions?.nativeElement === value?.nativeElement) return;
    this.actionsResizeObserver?.disconnect();
    this.inlineActions = value;
    if (value && typeof ResizeObserver !== 'undefined') {
      this.actionsResizeObserver = new ResizeObserver(() => this.measureFullTrail());
      this.actionsResizeObserver.observe(value.nativeElement);
    }
    this.scheduleFullTrailMeasurement();
  }
  private inlineActions?: ElementRef<HTMLElement>;
  @ViewChild('fullTrailMeasure') private fullTrailMeasure?: ElementRef<HTMLElement>;
  @ViewChild('collapsedTrigger') private collapsedTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('fullPathPopover', {read: ElementRef}) private fullPathPopover?: ElementRef<HTMLIonPopoverElement>;
  @ViewChild('navigationFullPath') private navigationFullPath?: ElementRef<HTMLElement>;
  @ViewChild('relocationFullPath') set relocationFullPathElement(value: ElementRef<HTMLElement> | undefined) {
    this.relocationFullPath = value;
    if (value && this.relocationFullPathOpen) value.nativeElement.scrollTop = this.fullPathScrollTop;
  }
  private relocationFullPath?: ElementRef<HTMLElement>;

  fullPathOpen = false;
  fullPathEvent?: Event;
  fullPathAnchorBottom = 0;
  relocationFullPathOpen = false;
  relocationFullPathLeft = 0;
  relocationFullPathTop = 0;
  private fullPathScrollTop = 0;
  private fullPathGap: {top: number; left: number} | null = null;
  private reanchorFrameId: number | null = null;
  private reanchorTimeoutId: number | null = null;
  private readonly handleVisualViewportResize = () => this.refreshFullPathPosition();
  private fullPathFits = false;
  private trailResizeObserver?: ResizeObserver;
  private actionsResizeObserver?: ResizeObserver;
  private destroyed = false;

  private longPressTimer: number | null = null;
  private longPressPointerId: number | null = null;
  private longPressBreadcrumbId: string | null = null;
  private longPressPointerEvent: PointerEvent | null = null;
  private longPressStartX = 0;
  private longPressStartY = 0;
  private suppressedClickBreadcrumbId: string | null = null;
  private suppressedClickResetTimer: number | null = null;

  get visibleBreadcrumbs(): ShellBreadcrumb[] {
    if (!this.isCollapsed) {
      return this.breadcrumbs;
    }

    return this.breadcrumbs.slice(-2);
  }

  get hasCollapsedAncestors(): boolean {
    return this.isCollapsed && (this.breadcrumbs.length > 2 || !this.fullPathFits);
  }

  get isCollapsed(): boolean {
    return this.compactMode && this.collapsed && !this.fullPathFits;
  }

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.scheduleFullTrailMeasurement();
    window.visualViewport?.addEventListener('resize', this.handleVisualViewportResize);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['breadcrumbs'] || changes['compactMode'] || changes['trailingActionTemplate']) {
      this.fullPathFits = false;
      this.scheduleFullTrailMeasurement();
    }
    if (!this.compactMode) this.fullPathOpen = false;
    if (!this.relocationMode) this.closeRelocationFullPath();
  }

  private scheduleFullTrailMeasurement(): void {
    queueMicrotask(() => this.measureFullTrail());
  }

  private measureFullTrail(): void {
    if (this.destroyed || !this.compactMode || !this.trail || !this.fullTrailMeasure) return;
    const actions = this.inlineActions?.nativeElement;
    const actionsWidth = actions
      ? Math.max(0, actions.getBoundingClientRect().width + parseFloat(getComputedStyle(actions).marginInlineEnd || '0'))
      : 0;
    const fullPathFits = this.fullTrailMeasure.nativeElement.getBoundingClientRect().width + actionsWidth <= this.trail.nativeElement.clientWidth;
    if (this.fullPathFits !== fullPathFits) {
      this.fullPathFits = fullPathFits;
      this.changeDetector.detectChanges();
    }
  }

  openFullPath(event: MouseEvent): void {
    if (this.relocationMode) { event.preventDefault(); return; }
    this.cancelFullPathReanchor();
    this.fullPathGap = null;
    this.fullPathPopover?.nativeElement.style.removeProperty('--offset-y');
    this.fullPathPopover?.nativeElement.style.removeProperty('--offset-x');
    this.fullPathAnchorBottom = Math.ceil((event.currentTarget as HTMLElement).getBoundingClientRect().bottom);
    this.fullPathEvent = event;
    this.fullPathOpen = true;
    this.fullPathOpened.emit();
  }

  async closeNavigationFullPath(): Promise<void> {
    if (!this.fullPathOpen) return;
    this.fullPathScrollTop = this.navigationFullPath?.nativeElement.scrollTop ?? this.fullPathScrollTop;
    this.fullPathOpen = false;
    await this.fullPathPopover?.nativeElement.dismiss();
  }

  reopenNavigationFullPath(): void {
    const trigger = this.collapsedTrigger?.nativeElement;
    if (trigger && !this.relocationMode) trigger.click();
  }

  openRelocationFullPath(): void {
    const trigger = this.collapsedTrigger?.nativeElement;
    if (!this.relocationMode || !trigger) return;
    this.fullPathOpen = false;
    const bounds = trigger.getBoundingClientRect();
    this.relocationFullPathLeft = Math.max(8, Math.min(bounds.left, window.innerWidth - 288));
    this.relocationFullPathTop = Math.max(8, Math.min(bounds.bottom + 8, window.innerHeight - 160));
    this.relocationFullPathOpen = true;
    this.fullPathOpened.emit();
  }

  closeRelocationFullPath(): void {
    if (!this.relocationFullPathOpen) return;
    this.fullPathScrollTop = this.relocationFullPath?.nativeElement.scrollTop ?? this.fullPathScrollTop;
    this.relocationFullPathOpen = false;
    this.fullPathClosed.emit();
  }

  isRelocationFullPathOpen(): boolean {
    return this.relocationFullPathOpen;
  }

  handleFullPathPresented(): void {
    const trigger = this.collapsedTrigger?.nativeElement;
    const content = this.fullPathPopover?.nativeElement.shadowRoot?.querySelector<HTMLElement>('[part="content"]');
    if (!this.fullPathOpen || !trigger || !content) return;
    const triggerRect = trigger.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    this.fullPathGap = {top: contentRect.top - triggerRect.bottom, left: contentRect.left - triggerRect.left};
    if (this.navigationFullPath) this.navigationFullPath.nativeElement.scrollTop = this.fullPathScrollTop;
  }

  refreshFullPathPosition(): void {
    if (!this.fullPathOpen || !this.fullPathGap) return;
    this.cancelFullPathReanchor();
    this.reanchorFrameId = window.requestAnimationFrame(() => {
      this.reanchorFrameId = window.requestAnimationFrame(() => this.reanchorFullPath());
    });
    // Android may finish restoring its WebView viewport after the first paint.
    this.reanchorTimeoutId = window.setTimeout(() => {
      this.reanchorFullPath();
      this.reanchorTimeoutId = window.setTimeout(() => this.reanchorFullPath(), 750);
    }, 250);
  }

  private reanchorFullPath(): void {
    const trigger = this.collapsedTrigger?.nativeElement;
    const popover = this.fullPathPopover?.nativeElement;
    const content = popover?.shadowRoot?.querySelector<HTMLElement>('[part="content"]');
    if (!this.fullPathOpen || !this.fullPathGap || !trigger?.isConnected || !content || !popover) return;

    const triggerRect = trigger.getBoundingClientRect();
    // Ionic's enter animation stores a pixel position once. Read that base with
    // our correction cleared so repeated resumes cannot accumulate stale offsets.
    popover.style.setProperty('--offset-y', '0px');
    popover.style.setProperty('--offset-x', '0px');
    const contentRect = content.getBoundingClientRect();
    const viewport = window.visualViewport;
    const inset = 12;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportLeft = viewport?.offsetLeft ?? 0;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const viewportWidth = viewport?.width ?? window.innerWidth;
    const top = Math.max(viewportTop + inset, Math.min(triggerRect.bottom + this.fullPathGap.top,
      viewportTop + viewportHeight - inset - contentRect.height));
    const left = Math.max(viewportLeft + inset, Math.min(triggerRect.left + this.fullPathGap.left,
      viewportLeft + viewportWidth - inset - contentRect.width));
    popover.style.setProperty('--offset-y', `${top - contentRect.top}px`);
    popover.style.setProperty('--offset-x', `${left - contentRect.left}px`);
    this.fullPathAnchorBottom = Math.ceil(triggerRect.bottom);
  }

  private cancelFullPathReanchor(): void {
    if (this.reanchorFrameId !== null) window.cancelAnimationFrame(this.reanchorFrameId);
    if (this.reanchorTimeoutId !== null) window.clearTimeout(this.reanchorTimeoutId);
    this.reanchorFrameId = null;
    this.reanchorTimeoutId = null;
  }

  handleFullPathDismissed(): void {
    this.cancelFullPathReanchor();
    this.fullPathOpen = false;
    this.fullPathClosed.emit();
    this.fullPathGap = null;
    this.fullPathPopover?.nativeElement.style.removeProperty('--offset-y');
    this.fullPathPopover?.nativeElement.style.removeProperty('--offset-x');
    if (this.collapsedTrigger?.nativeElement.isConnected) this.collapsedTrigger.nativeElement.focus();
  }

  handleFullPathClick(event: MouseEvent, breadcrumb: ShellBreadcrumb): void {
    if (this.relocationMode) { event.preventDefault(); return; }
    this.handleBreadcrumbClick(event, breadcrumb);
    if (event.defaultPrevented) void this.closeNavigationFullPath();
  }

  isFirstBreadcrumb(breadcrumb: ShellBreadcrumb): boolean {
    return this.breadcrumbs[0]?.id === breadcrumb.id;
  }

  getFullPathIconName(breadcrumb: ShellBreadcrumb): string | null {
    if (breadcrumb.icon) return breadcrumb.icon;
    if (breadcrumb.id === 'home' && breadcrumb.route === '/home') return 'home-outline';
    if (breadcrumb.route === '/inventory/containers' || isShellContainerBreadcrumb(breadcrumb)) return 'file-tray-outline';
    return this.iconResolver(breadcrumb);
  }

  isCurrentBreadcrumb(breadcrumb: ShellBreadcrumb): boolean {
    return this.breadcrumbs[this.breadcrumbs.length - 1]?.id === breadcrumb.id;
  }

  handleBreadcrumbClick(event: MouseEvent, breadcrumb: ShellBreadcrumb): void {
    if (this.relocationMode) { event.preventDefault(); return; }
    if (this.suppressedClickBreadcrumbId === breadcrumb.id) {
      this.clearSuppressedClick();
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    this.breadcrumbSelected.emit(breadcrumb.route);
  }

  handleContextMenu(event: MouseEvent, breadcrumb: ShellBreadcrumb): void {
    if (!isShellContainerBreadcrumb(breadcrumb)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (
      this.longPressTimer !== null
      && this.longPressBreadcrumbId === breadcrumb.id
      && this.longPressPointerEvent
    ) {
      this.completeLongPress(breadcrumb, this.longPressPointerEvent);
    }
  }

  handlePointerDown(event: PointerEvent, breadcrumb: ShellBreadcrumb): void {
    if (this.relocationMode || !isShellContainerBreadcrumb(breadcrumb) || !event.isPrimary || event.button !== 0) {
      return;
    }

    this.cancelLongPress();
    this.longPressPointerId = event.pointerId;
    this.longPressBreadcrumbId = breadcrumb.id;
    this.longPressPointerEvent = event;
    this.longPressStartX = event.clientX;
    this.longPressStartY = event.clientY;
    this.longPressTimer = window.setTimeout(() => {
      this.completeLongPress(breadcrumb, event);
    }, SIBLING_SWITCHER_LONG_PRESS_MS);
  }

  handlePointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.longPressPointerId || this.longPressTimer === null) {
      return;
    }

    if (
      Math.abs(event.clientX - this.longPressStartX) > SIBLING_SWITCHER_MOVE_TOLERANCE_PX
      || Math.abs(event.clientY - this.longPressStartY) > SIBLING_SWITCHER_MOVE_TOLERANCE_PX
    ) {
      this.cancelLongPress();
    }
  }

  handlePointerEnd(event: PointerEvent): void {
    if (event.pointerId === this.longPressPointerId) {
      this.cancelLongPress();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.closeRelocationFullPath();
    this.cancelFullPathReanchor();
    window.visualViewport?.removeEventListener('resize', this.handleVisualViewportResize);
    this.trailResizeObserver?.disconnect();
    this.actionsResizeObserver?.disconnect();
    this.cancelLongPress();
    this.clearSuppressedClick();
  }

  getBreadcrumbShapeVariant(label: string, isFirst: boolean, isCurrent: boolean): ShellBreadcrumbShapeVariant {
    if (isFirst && isCurrent) {
      return 'single';
    }

    if (isFirst) {
      return 'compact';
    }

    const normalizedLength = label.trim().length;
    if (normalizedLength <= 3) {
      return 'compact';
    }

    if (normalizedLength <= 10) {
      return 'short';
    }

    if (normalizedLength <= 18) {
      return 'medium';
    }

    return 'long';
  }

  getBreadcrumbPath(shapeVariant: ShellBreadcrumbShapeVariant): string {
    const bodyEnd =
      shapeVariant === 'compact'
        ? this.compactBodyEndX
        : shapeVariant === 'short'
          ? this.shortBodyEndX
        : shapeVariant === 'medium'
          ? this.mediumBodyEndX
          : this.longBodyEndX;

    return `M ${this.edgeInsetX} 0 H ${bodyEnd} Q 196 0 ${this.edgeTipX} 20 Q 196 40 ${bodyEnd} 40 H ${this.edgeInsetX} Q 4 40 0 20 Q 4 0 ${this.edgeInsetX} 0 Z`;
  }

  getBreadcrumbHref(route: string): string {
    return this.routeHrefBuilder(route);
  }

  getRelocationDropTargetId(breadcrumb: ShellBreadcrumb): string | null {
    const target = breadcrumb.relocationDropTarget;
    if (!target) {
      return null;
    }

    return target.destinationContainerId ?? 'root';
  }

  canAcceptRelocation(breadcrumb: ShellBreadcrumb): boolean {
    const target = breadcrumb.relocationDropTarget;
    if (!this.relocationMode || !target) return false;
    if (this.relocationSourceKind === 'item' && target.accepts !== 'all') return false;
    return this.relocationSourceKind !== 'container' || target.destinationContainerId !== this.relocationSourceId;
  }

  showBreadcrumbIcon(breadcrumb: ShellBreadcrumb, isFirst: boolean): boolean {
    return isFirst || breadcrumb.route === '/home' || breadcrumb.id === 'home';
  }

  isContainerBreadcrumb(breadcrumb: ShellBreadcrumb): breadcrumb is ShellContainerBreadcrumb {
    return isShellContainerBreadcrumb(breadcrumb);
  }

  private cancelLongPress(): void {
    if (this.longPressTimer !== null) {
      window.clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    this.longPressPointerId = null;
    this.longPressBreadcrumbId = null;
    this.longPressPointerEvent = null;
  }

  private completeLongPress(breadcrumb: ShellContainerBreadcrumb, event: PointerEvent): void {
    if (this.longPressTimer !== null) {
      window.clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    this.longPressPointerId = null;
    this.longPressBreadcrumbId = null;
    this.longPressPointerEvent = null;
    this.suppressedClickBreadcrumbId = breadcrumb.id;
    this.scheduleSuppressedClickReset();
    this.breadcrumbLongPressed.emit({breadcrumb, event});
  }

  private scheduleSuppressedClickReset(): void {
    if (this.suppressedClickResetTimer !== null) {
      window.clearTimeout(this.suppressedClickResetTimer);
    }

    this.suppressedClickResetTimer = window.setTimeout(() => {
      this.suppressedClickBreadcrumbId = null;
      this.suppressedClickResetTimer = null;
    }, 1000);
  }

  private clearSuppressedClick(): void {
    this.suppressedClickBreadcrumbId = null;
    if (this.suppressedClickResetTimer !== null) {
      window.clearTimeout(this.suppressedClickResetTimer);
      this.suppressedClickResetTimer = null;
    }
  }
}
